import { useState } from 'react';
import {
  CheckCircle2,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Unlink2,
  UserPlus,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useAdminCustomerUsers,
  useAttachUserToCustomer,
  useCreateCustomerUser,
  useDetachUserFromCustomer,
  useSendWelcomeEmail,
  useSetMembershipRole,
  useUpdateCustomerUser,
  type MembershipRole,
  type AdminCustomerUserRow,
} from '@/hooks/admin/useAdminCustomerUsers';
import { ROLE_OPTIONS, roleLabel } from './roles';

/**
 * CustomerUsersPanel — the per-customer team-management surface (invite /
 * attach existing / set role / resend welcome / detach), scoped to ONE
 * customer. Extracted from the old standalone /admin/users page; the customer
 * is now fixed by the detail page, so there's no customer picker.
 *
 * All actions call the existing admin backend functions unchanged:
 *   - admin-create-customer-user
 *   - admin-attach-user-to-customer
 *   - admin-set-membership-role
 *   - admin-send-welcome-email
 *   - admin-detach-user-from-customer
 */

function randomTempPassword(): string {
  // Must satisfy the Supabase password policy (shared project-wide with the
  // gateway): at least one lowercase, uppercase, digit AND symbol — an
  // alphanumeric-only password 422s with weak_password. Ambiguous chars
  // (I/l/O/0/1) excluded for readability.
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%^&*-_=+';
  const all = lower + upper + digits + symbols;
  const rnd = new Uint32Array(32);
  crypto.getRandomValues(rnd);
  let i = 0;
  const next = (set: string) => set[rnd[i++]! % set.length]!;
  const chars = [next(lower), next(upper), next(digits), next(symbols)];
  while (chars.length < 14) chars.push(next(all));
  // Shuffle so the guaranteed-class characters aren't always the first four.
  for (let j = chars.length - 1; j > 0; j--) {
    const k = rnd[i++]! % (j + 1);
    [chars[j], chars[k]] = [chars[k]!, chars[j]!];
  }
  return chars.join('');
}

export function CustomerUsersPanel({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const [showRemoved, setShowRemoved] = useState(false);
  const { data: usersData, isLoading, refetch } = useAdminCustomerUsers(customerId, showRemoved);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2">
        {/* A detached user is hidden, not deleted — this is how you find one
            again without having to remember their exact address. */}
        <Button
          variant={showRemoved ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setShowRemoved((v) => !v)}
          title="Include users who were removed from this account"
        >
          {showRemoved ? 'Hide removed' : 'Show removed'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => void refetch()} title="Refresh users">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>
      <CreateUserForm customerId={customerId} customerName={customerName} />
      <AttachExistingUserForm customerId={customerId} customerName={customerName} />
      <UsersTable
        users={usersData?.rows ?? []}
        loading={isLoading}
        customerId={customerId}
        customerName={customerName}
      />
    </div>
  );
}

/* -------------------- Create user form -------------------- */

function CreateUserForm({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const create = useCreateCustomerUser();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(() => randomTempPassword());
  const [role, setRole] = useState<MembershipRole>('member');
  const [sendWelcome, setSendWelcome] = useState(true);
  const [created, setCreated] = useState<{ email: string; password: string; emailed: boolean } | null>(null);

  function reset() {
    setFullName('');
    setPhone('');
    setEmail('');
    setPassword(randomTempPassword());
    setRole('member');
    setSendWelcome(true);
    create.reset();
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCreated(null);
    try {
      const res = await create.mutateAsync({
        customer_id: customerId,
        email: email.trim().toLowerCase(),
        password,
        full_name: fullName.trim(),
        phone: phone.trim() || undefined,
        role,
        send_welcome: sendWelcome,
      });
      setCreated({ email: res.email, password, emailed: res.email_sent });
      setFullName('');
      setPhone('');
      setEmail('');
      setPassword(randomTempPassword());
    } catch {
      // create.error surfaces the message via the form below.
    }
  }

  const errorMessage = create.error instanceof Error ? create.error.message : null;

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
      <div className="px-5 py-3 border-b border-brix-border bg-brix-surface-alt/60 flex items-center justify-between">
        <h2 className="font-display text-base font-bold text-brix-ink flex items-center gap-2">
          <UserPlus size={16} className="text-brix-primary" />
          Invite user for {customerName}
        </h2>
        {created && (
          <button
            type="button"
            onClick={reset}
            className="text-xs text-brix-muted hover:text-brix-ink cursor-pointer"
          >
            New invite
          </button>
        )}
      </div>

      {created ? (
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 size={16} />
            <span className="text-sm font-semibold">User created</span>
          </div>
          <div className="text-sm text-brix-muted">
            {created.emailed
              ? <>The welcome email was sent to <span className="font-mono text-brix-ink">{created.email}</span>. The temp password below was emailed — copy it now if you also want to share it directly.</>
              : <>The welcome email could not be sent automatically. Share these credentials with the customer manually.</>
            }
          </div>
          <CredentialBlock label="Email" value={created.email} />
          <CredentialBlock label="Temp password" value={created.password} mono />
        </div>
      ) : (
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="cu-name">Full name</Label>
            <Input
              id="cu-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Maria Reyes"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cu-phone">Phone (caller recognition)</Label>
            <Input
              id="cu-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(510) 555-1234"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cu-email">Email</Label>
            <Input
              id="cu-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="maria@example.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cu-password">Temp password</Label>
            <div className="flex gap-2">
              <Input
                id="cu-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="font-mono"
              />
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => setPassword(randomTempPassword())}
                title="Generate"
              >
                <RefreshCw size={14} />
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cu-role">Role</Label>
            <select
              id="cu-role"
              className={cn(
                'flex h-11 w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-base cursor-pointer',
                'focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/30',
              )}
              value={role}
              onChange={(e) => setRole(e.target.value as MembershipRole)}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm text-brix-ink cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendWelcome}
              onChange={(e) => setSendWelcome(e.target.checked)}
              className="rounded border-brix-border cursor-pointer"
            />
            Email the welcome message with these credentials
          </label>

          {errorMessage && (
            <div className="md:col-span-2 rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
              {errorMessage}
            </div>
          )}

          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" size="lg" disabled={create.isPending}>
              {create.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Creating…
                </>
              ) : (
                <>
                  <UserPlus size={14} /> Create user
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/* -------------------- Attach existing user -------------------- */

function AttachExistingUserForm({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const attach = useAttachUserToCustomer();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MembershipRole>('member');
  const [result, setResult] = useState<{ email: string; action: string } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResult(null);
    try {
      const res = await attach.mutateAsync({
        email: email.trim().toLowerCase(),
        customer_id: customerId,
        role,
      });
      setResult({ email: res.email, action: res.action });
      setEmail('');
    } catch {
      // attach.error surfaces below
    }
  }

  const errorMessage = attach.error instanceof Error ? attach.error.message : null;

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
      <div className="px-5 py-3 border-b border-brix-border bg-brix-surface-alt/60">
        <h2 className="font-display text-sm font-semibold text-brix-ink flex items-center gap-2">
          <Link2 size={14} className="text-brix-primary" />
          Attach an existing user to {customerName}
        </h2>
        <p className="text-[11px] text-brix-muted mt-0.5">
          Use this when the user already exists (e.g. they were invited to another customer)
          and now needs access to this one too. They keep their existing password.
        </p>
      </div>

      <form onSubmit={onSubmit} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label htmlFor="attach-email">Existing user email</Label>
          <Input
            id="attach-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="maria@example.com"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="attach-role">Role here</Label>
          <select
            id="attach-role"
            className={cn(
              'flex h-11 w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-base cursor-pointer',
              'focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/30',
            )}
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <Button type="submit" size="md" disabled={attach.isPending}>
          {attach.isPending ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
          Attach
        </Button>

        {result && (
          <div className="md:col-span-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {result.action === 'already_active'
              ? `${result.email} is already attached to ${customerName}.`
              : result.action === 'reactivated'
                ? `Reactivated ${result.email}'s membership in ${customerName}.`
                : `Attached ${result.email} to ${customerName}.`}
          </div>
        )}

        {errorMessage && (
          <div className="md:col-span-3 rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
            {errorMessage}
          </div>
        )}
      </form>
    </div>
  );
}

/* -------------------- Users table -------------------- */

function UsersTable({
  users,
  loading,
  customerId,
  customerName,
}: {
  users: AdminCustomerUserRow[];
  loading: boolean;
  customerId: string;
  customerName: string;
}) {
  const send = useSendWelcomeEmail();
  const detach = useDetachUserFromCustomer();
  const setRole = useSetMembershipRole();
  const attach = useAttachUserToCustomer();
  const [editing, setEditing] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<{ userId: string; ok: boolean; message: string } | null>(null);

  async function onResetAndEmail(userId: string, email: string | null) {
    setResendStatus(null);
    const password = randomTempPassword();
    try {
      const res = await send.mutateAsync({ user_id: userId, password });
      setResendStatus({
        userId,
        ok: true,
        message: `New temp password emailed to ${res.email}. They'll set their own on next sign-in.`,
      });
    } catch (err) {
      setResendStatus({
        userId,
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      });
    }
    void email;
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
      <div className="px-5 py-3 border-b border-brix-border bg-brix-surface-alt/60">
        <h2 className="font-display text-sm font-semibold text-brix-ink">
          Users at {customerName}
        </h2>
      </div>

      <div className="grid grid-cols-[1fr_140px_140px_180px] gap-4 px-5 py-2.5 text-[11px] font-semibold tracking-tight text-brix-muted border-b border-brix-border">
        <div>Person</div>
        <div>Role</div>
        <div>Last sign-in</div>
        <div className="text-right">Actions</div>
      </div>

      {loading ? (
        <div className="px-5 py-8 text-center text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : users.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-brix-muted">
          No users yet. Create one above.
        </div>
      ) : (
        users.map((u) => (
          <div
            key={u.id}
            className={cn(
              'border-b border-brix-border last:border-b-0',
              !u.active && 'bg-brix-surface-alt/40',
            )}
          >
            <div className="grid grid-cols-[1fr_140px_140px_180px] gap-4 px-5 py-3 items-center">
              <div className="min-w-0">
                <div className="font-medium text-brix-ink truncate">
                  {u.full_name ?? '—'}
                  {u.is_superadmin && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] tracking-tight rounded bg-brix-primary-tint text-brix-primary font-semibold">
                      <ShieldCheck size={10} /> superadmin
                    </span>
                  )}
                  {!u.active && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] tracking-tight rounded bg-brix-border/60 text-brix-muted font-semibold">
                      removed
                    </span>
                  )}
                </div>
                <div className="text-xs text-brix-muted truncate">{u.email ?? '(no email on auth user)'}</div>
              </div>
              <div>
                <RoleSelect
                  value={u.role}
                  /* A removed membership's role is set by Restore, so editing
                     it here would look like a change that does nothing. */
                  disabled={setRole.isPending || !u.active}
                  onChange={(next) => {
                    if (next !== u.role) {
                      setRole.mutate({ user_id: u.id, customer_id: customerId, role: next });
                    }
                  }}
                />
                {/* Accounts payable ON A GROUP MASTER *is* the all-stores
                    login — the role carries it, so there is no second box to
                    tick and nothing to keep in sync. */}
                {u.role === 'ap' && (
                  <p className="mt-1 text-[11px] leading-tight text-brix-muted">
                    Sees every store on a chain account
                  </p>
                )}
              </div>
              <div className="text-xs text-brix-muted">
                {u.last_sign_in_at
                  ? new Date(u.last_sign_in_at).toLocaleDateString()
                  : 'Never'}
              </div>
              <div className="flex justify-end gap-1.5">
                {!u.active ? (
                  /* Removed, not deleted. Restore goes through the ordinary
                     attach endpoint, which reactivates this same membership —
                     so their order history and audit trail stay on the same
                     login rather than starting again on a new one. */
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={attach.isPending || !u.email}
                    onClick={() => {
                      if (!u.email) return;
                      void attach.mutateAsync({
                        customer_id: customerId,
                        email: u.email,
                        role: (u.role as MembershipRole) ?? 'member',
                      });
                    }}
                    title="Put this user back on the account"
                  >
                    {attach.isPending ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RotateCcw size={12} />
                    )}
                    Restore
                  </Button>
                ) : (
                <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditing(editing === u.id ? null : u.id)}
                  title="Change their email or name"
                >
                  <Pencil size={12} /> Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={send.isPending}
                  onClick={() => void onResetAndEmail(u.id, u.email ?? null)}
                  title="Generate a fresh temp password and email it"
                >
                  {send.isPending && send.variables?.user_id === u.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <KeyRound size={12} />
                  )}
                  Reset & email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={detach.isPending}
                  onClick={() => {
                    const ok = window.confirm(
                      `Detach ${u.full_name ?? u.email ?? 'this user'} from ${customerName}? ` +
                        `Their other memberships (if any) are unaffected.`,
                    );
                    if (!ok) return;
                    void detach.mutateAsync({ user_id: u.id, customer_id: customerId });
                  }}
                  title="Remove from this customer (reversible)"
                >
                  <Unlink2 size={12} />
                </Button>
                </>
                )}
              </div>
            </div>

            {editing === u.id && u.active && (
              <EditUserRow
                user={u}
                customerId={customerId}
                onDone={() => setEditing(null)}
              />
            )}
            {resendStatus && resendStatus.userId === u.id && (
              <div
                className={cn(
                  'px-5 pb-3 text-xs',
                  resendStatus.ok ? 'text-emerald-700' : 'text-brix-warning',
                )}
              >
                {resendStatus.message}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

/**
 * Edit a user in place — their EMAIL (which is their login) and their name.
 *
 * ⚠ This is the thing whose absence forced "make a new user and remove the
 * old one": on a single-user account that locks the real person out and
 * strands their history on a login nobody uses. Editing keeps the same auth
 * user, so password, memberships, orders and audit trail all follow.
 */
function EditUserRow({
  user,
  customerId,
  onDone,
}: {
  user: AdminCustomerUserRow;
  customerId: string;
  onDone: () => void;
}) {
  const update = useUpdateCustomerUser();
  const [email, setEmail] = useState(user.email ?? '');
  const [fullName, setFullName] = useState(user.full_name ?? '');
  const [done, setDone] = useState<string | null>(null);

  const emailChanged = email.trim().toLowerCase() !== (user.email ?? '').toLowerCase();
  const nameChanged = fullName.trim() !== (user.full_name ?? '');
  const dirty = emailChanged || nameChanged;

  function save() {
    if (!dirty) return;
    setDone(null);
    update.mutate(
      {
        customer_id: customerId,
        user_id: user.id,
        ...(emailChanged ? { email: email.trim().toLowerCase() } : {}),
        ...(nameChanged ? { full_name: fullName.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          setDone(
            res.email_changed
              ? `Saved. They sign in with ${res.email} from now on — ${res.old_email} no longer works.`
              : 'Saved.',
          );
        },
      },
    );
  }

  return (
    <div className="px-5 pb-4 pt-1 bg-brix-surface-alt/40 border-t border-brix-border/60">
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <Label htmlFor={`eu-email-${user.id}`}>Email (their login)</Label>
          <Input
            id={`eu-email-${user.id}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
          />
        </div>
        <div>
          <Label htmlFor={`eu-name-${user.id}`}>Name</Label>
          <Input
            id={`eu-name-${user.id}`}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name"
          />
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" disabled={!dirty || update.isPending} onClick={save}>
            {update.isPending ? <Loader2 size={12} className="animate-spin" /> : null} Save
          </Button>
          <Button variant="ghost" size="sm" onClick={onDone} title="Close">
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* Said before the save, not discovered after it. */}
      {emailChanged && (
        <p className="mt-2 text-[11px] leading-relaxed text-amber-700">
          Changing the email changes how they sign in. The old address stops working
          straight away, and any password-reset link already in their inbox is void.
          Their password itself is unchanged.
        </p>
      )}
      {update.error && (
        <p className="mt-2 text-xs text-brix-warning">{update.error.message}</p>
      )}
      {done && <p className="mt-2 text-xs text-emerald-700">{done}</p>}
    </div>
  );
}

/**
 * Role <select>. Offers the canonical Member / Admin (/ AP) options, but if the
 * row carries a legacy value (e.g. 'location_manager') we surface it as an
 * extra option so the control stays readable until the next save.
 */
function RoleSelect({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (next: MembershipRole) => void;
}) {
  const isKnown = ROLE_OPTIONS.some((o) => o.value === value);
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as MembershipRole)}
      className="h-8 px-2 rounded-md border border-brix-border bg-brix-surface text-sm text-brix-ink tracking-tight cursor-pointer disabled:opacity-50"
      aria-label="Role"
    >
      {!isKnown && <option value={value}>{roleLabel(value)}</option>}
      {ROLE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

/* -------------------- Credential block -------------------- */

function CredentialBlock({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/60 px-3 py-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs tracking-tight text-brix-muted font-semibold">{label}</div>
        <div className={cn('text-base text-brix-ink truncate', mono && 'font-mono')}>{value}</div>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
